import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import {
    AlertTriangle,
    Archive,
    ArrowUpRight,
    Ban,
    BarChart3,
    ChevronRight,
    Clock,
    DollarSign,
    Gift,
    Info,
    Layers,
    Lock,
    Package,
    PieChart,
    Percent,
    RefreshCw,
    Tag,
    TrendingDown,
    TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import { useAppBarAction, useAppBarSubtitle } from '../../component/layout/AppBarActions';
import { KitCard, SecH } from '../../component/common/MobileKit';
import { HBars, Kpi, KpiGrid, KitEmptyNote, formatMoneyShort } from '../../component/common/MobileDashboardKit';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { usePermissions } from '../../hooks/usePermissions';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DialogForm from '../../component/dialog/DialogForm';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import KpiTickerCard from '../../component/report/KpiTickerCard';
import {
    BarRow,
    CardHeadRow,
    ChartLegendItem,
    EmptyMiniState,
    ListRow,
    Pill,
    RankNum,
    SummaryRow,
    fmtInt,
    fmtPct1,
    isoToBr,
    isoToDm,
    pctOf,
} from './CustomerDashboard';
import { IndicatorGlossaryButton } from '../../component/common/IndicatorHelp';
import { DashboardBoard, DashboardEditBar, DashboardLayoutChip, DashboardPersonalizeButton, Indicator, useDashboardLayout } from '../../component/common/DashboardCustomize';
import { PRODUCT_INDICATOR_HELP as HELP, PRODUCT_GLOSSARY_ORDER } from './productIndicatorHelp';
import './dashboard.css';

const GLOSSARY_ITEMS = PRODUCT_GLOSSARY_ORDER.map((k) => HELP[k]);

// Indicadores personalizáveis (desktop), na ordem de exibição. Ids são persistidos no backend: não renomear.
// w = largura padrão em colunas da grade de 12; minW = largura mínima (a altura mínima é a do conteúdo).
const LAYOUT_REGISTRY = [
    { id: 'a-ruptura', label: 'Alerta: produtos em ruptura', w: 3, minW: 3 },
    { id: 'a-baixo', label: 'Alerta: com estoque baixo', w: 3, minW: 3 },
    { id: 'a-margem', label: 'Alerta: abaixo da margem esperada', w: 3, minW: 3 },
    { id: 'a-parado', label: 'Alerta: capital parado', w: 3, minW: 3 },
    { id: 'receita', label: 'Receita de produtos', w: 6, minW: 4 },
    { id: 'lucro', label: 'Lucro líquido', w: 3, minW: 3 },
    { id: 'margem', label: 'Margem média', w: 3, minW: 3 },
    { id: 'unidades', label: 'Unidades vendidas', w: 3, minW: 3 },
    { id: 'custo', label: 'Custo dos produtos vendidos', w: 3, minW: 3 },
    { id: 'estoque', label: 'Valor em estoque (custo)', w: 3, minW: 3 },
    { id: 'giro', label: 'Giro de estoque', w: 3, minW: 3 },
    { id: 'cobertura', label: 'Cobertura de estoque', w: 3, minW: 3 },
    { id: 'ativos', label: 'Produtos ativos', w: 3, minW: 3 },
    { id: 'estoque-baixo', label: 'Estoque baixo', w: 3, minW: 3 },
    { id: 'ruptura', label: 'Produtos em ruptura', w: 3, minW: 3 },
    { id: 'sem-venda', label: 'Sem venda no período', w: 3, minW: 3 },
    { id: 'capital-parado', label: 'Capital parado', w: 3, minW: 3 },
    { id: 'abaixo-margem', label: 'Abaixo da margem esperada', w: 3, minW: 3 },
    { id: 'ganho-potencial', label: 'Ganho potencial c/ preços sugeridos', w: 3, minW: 3 },
    { id: 'prejuizo', label: 'Vendendo com prejuízo', w: 3, minW: 3 },
    { id: 'promocao', label: 'Produtos em promoção', w: 3, minW: 3 },
    { id: 'grafico-receita-custo', label: 'Gráfico: Receita x custo', w: 6, minW: 4 },
    { id: 'grafico-margem', label: 'Gráfico: Margem média ao longo do tempo', w: 6, minW: 4 },
    { id: 'mais-vendidos', label: 'Produtos mais vendidos', w: 7, minW: 4 },
    { id: 'curva-abc', label: 'Curva ABC de produtos', w: 5, minW: 4 },
    { id: 'categorias', label: 'Receita e margem por categoria', w: 6, minW: 4 },
    { id: 'lista-abaixo-margem', label: 'Lista: abaixo da margem esperada', w: 6, minW: 4 },
    { id: 'lista-estoque-baixo', label: 'Lista: estoque baixo e ruptura', w: 6, minW: 4 },
    { id: 'lista-sem-venda', label: 'Lista: sem venda no período', w: 6, minW: 4 },
];

/**
 * Dashboard de Produtos (aba "Produtos" do hub /dashboards/product).
 * Ref: `ui-ux/prototipo-dashboard-produtos.html`. Todos os números vêm de
 * `GET /dashboard/product` (fv-api/report/product_dashboard.go) — aqui só há
 * formatação, razões de exibição e agrupamento das séries diárias em dia/semana/mês.
 *
 * Regras do backend (rótulos fiéis ao cálculo):
 * - venda = item de venda "Finalizada" + item de produto de OS "Paga";
 * - catálogo = produtos ativos e não genéricos (genérico não controla estoque);
 * - ruptura = saldo <= 0; estoque baixo = saldo > 0 e abaixo do mínimo configurado;
 * - giro = custo vendido ÷ estoque médio a custo; cobertura = estoque médio ÷ custo vendido por dia;
 * - capital parado = saldo × custo dos produtos sem venda no período;
 * - saldos/valores de estoque em datas passadas são reconstruídos pelas movimentações, com
 *   custo/preço atuais (não há snapshot histórico) — por isso a variação de estoque é "est.";
 * - variação vs. período anterior só existe onde o backend consegue recalcular o período
 *   anterior (vendas e saldos). Abaixo da margem, ganho potencial, promoções e produtos ativos
 *   são posição atual, sem histórico.
 */

const REPORT_PRODUCTS = '/reports/products';
const REPORT_STOCK = '/reports/stock';
const PRODUCTS_LIST = '/products';

const fmtBrl = (cents) => `R$ ${((cents || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtBrl0 = (cents) => `R$ ${Math.round((cents || 0) / 100).toLocaleString('pt-BR')}`;
const fmtBrlShort = (cents) => {
    const v = (cents || 0) / 100;
    return v >= 1000 ? `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : `R$ ${Math.round(v)}`;
};
const fmtDays = (v) => (v == null ? '—' : `${Math.round(v).toLocaleString('pt-BR')} ${Math.round(v) === 1 ? 'dia' : 'dias'}`);
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Agrupa a série diária conforme o tamanho do período (diário <= 31 dias, semanal <= 120, mensal acima).
function buildBuckets(dates) {
    const n = dates.length;
    const groups = [];
    if (n <= 31) {
        dates.forEach((d, i) => groups.push({ label: isoToDm(d), idx: [i] }));
        return { groups, unit: 'dia' };
    }
    if (n <= 120) {
        for (let i = 0; i < n; i += 7) {
            const idx = [];
            for (let j = i; j < Math.min(i + 7, n); j += 1) idx.push(j);
            groups.push({ label: isoToDm(dates[i]), idx });
        }
        return { groups, unit: 'semana' };
    }
    let last = null;
    dates.forEach((d, i) => {
        const [y, m] = d.split('-');
        const key = `${y}-${m}`;
        if (!last || last.key !== key) {
            last = { key, label: `${MONTHS[Number(m) - 1]}/${y.slice(2)}`, idx: [] };
            groups.push(last);
        }
        last.idx.push(i);
    });
    return { groups, unit: 'mês' };
}

const sumAt = (arr, idx) => idx.reduce((acc, i) => acc + (arr[i] || 0), 0);

// Link/atalho que só vira navegação se o usuário tem a permissão do destino.
function AttentionItem({ color, bg, icon, value, label, to, allowed, title }) {
    const content = (
        <>
            <Box sx={{ width: 34, height: 34, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: bg, color }}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography component="b" sx={{ display: 'block', fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1, color: 'text.primary' }}>{value}</Typography>
                <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
            </Box>
            {allowed && <Box sx={{ ml: 'auto', color: 'text.disabled', display: 'flex' }}><ChevronRight size={16} strokeWidth={2} /></Box>}
        </>
    );
    const sx = {
        display: 'flex', alignItems: 'center', gap: '12px', p: '12px 14px', borderRadius: '12px', border: '1px solid', borderColor: 'divider',
        borderLeft: '3px solid', borderLeftColor: color, bgcolor: 'background.paper', textAlign: 'left', font: 'inherit', color: 'text.primary', textDecoration: 'none',
        transition: '.15s', minWidth: 0,
        ...(allowed && { cursor: 'pointer', '&:hover': { borderColor: color, boxShadow: 1 } }),
    };
    return allowed
        ? <Box component={RouterLink} to={to} title={title} sx={sx}>{content}</Box>
        : <Box title={title} sx={sx}>{content}</Box>;
}

function ReportLink({ to, title }) {
    return (
        <Box
            component={RouterLink}
            to={to}
            title={title}
            sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11.5px', fontWeight: 700, color: 'primary.main', textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}
        >
            Ver no relatório <ArrowUpRight size={13} strokeWidth={2} />
        </Box>
    );
}

const FootNote = ({ icon, children }) => (
    <Typography component="div" sx={{ mt: 1, pt: '10px', borderTop: '1px dashed', borderColor: 'divider', fontSize: '11.5px', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1, '& svg': { flexShrink: 0, color: 'secondary.main' }, '& b': { color: 'text.primary' } }}>
        {icon}<span>{children}</span>
    </Typography>
);

// Linha de lista com selo numérico/avatar à esquerda (estoque baixo, sem venda, abaixo da margem).
const Badge = ({ bg, fg, children }) => (
    <Box sx={{ width: 34, height: 34, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, bgcolor: bg, color: fg }}>{children}</Box>
);

const chartSx = (theme) => ({ '& .MuiChartsGrid-line': { stroke: theme.palette.divider }, '& .MuiChartsAxis-directionY': { display: 'none' } });

export default function ProductDashboard() {
    const theme = useTheme();
    const isMobileShell = useIsMobileShell();
    const { hasPermission } = usePermissions();
    const canView = hasPermission('dashboard.product');
    const canApplyPrice = hasPermission('report.product.pricing');
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [startDate, setStartDate] = useState(dayjs().subtract(29, 'day').startOf('day'));
    const [endDate, setEndDate] = useState(dayjs().endOf('day'));
    const requestId = useRef(0);
    const layout = useDashboardLayout('product', LAYOUT_REGISTRY, canView && !isMobileShell);
    const [applyProduct, setApplyProduct] = useState(null);
    const [applyLoading, setApplyLoading] = useState(false);
    const [severity, setSeverity] = useState('success');
    const [message, setMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const loadData = useCallback(async () => {
        if (!canView) {
            setLoading(false);
            return;
        }
        if (!startDate?.isValid?.() || !endDate?.isValid?.()) return;
        const id = ++requestId.current;
        setLoading(true);
        setFailed(false);
        try {
            const response = await httpClient.get(`/dashboard/product?startDate=${startDate.format('DD/MM/YYYY')}&endDate=${endDate.format('DD/MM/YYYY')}`);
            if (id !== requestId.current) return;
            if (response.status >= 200 && response.status < 300 && response.data) {
                setData(response.data);
            } else {
                setData(null);
                setFailed(true);
            }
        } catch (error) {
            console.error('Erro ao carregar o dashboard de produtos:', error);
            if (id === requestId.current) {
                setData(null);
                setFailed(true);
            }
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [startDate, endDate, canView]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useAppBarAction({
        label: 'Atualizar',
        icon: RefreshCw,
        onClick: loadData,
        disabled: loading,
        spinning: loading,
        enabled: isMobileShell,
    });
    useAppBarSubtitle(isMobileShell ? 'Dashboard' : null);

    // Mesma chamada da aba "Abaixo da margem" do relatório; o servidor recalcula o preço no momento.
    const handleApplySuggestedPrice = async () => {
        const row = applyProduct;
        if (!row?.product_id) return;
        setApplyLoading(true);
        try {
            const response = await httpClient.post('/chart/product/below-margin/apply', { product_id: row.product_id, confirm: true });
            if (response.status === 204) {
                setApplyProduct(null);
                setSeverity('success');
                setMessage('Preço sugerido aplicado com sucesso.');
                setOpenSnackbar(true);
                await loadData();
                return;
            }
            setApplyProduct(null);
            setSeverity('error');
            setMessage((typeof response.data === 'string' && response.data.trim()) || 'Não foi possível aplicar o preço sugerido.');
            setOpenSnackbar(true);
        } catch (error) {
            setApplyProduct(null);
            setSeverity('error');
            setMessage((typeof error.response?.data === 'string' && error.response.data.trim()) || 'Erro ao aplicar o preço sugerido. Tente novamente.');
            setOpenSnackbar(true);
        } finally {
            setApplyLoading(false);
        }
    };

    const cur = data?.current;
    const prev = data?.previous;
    const series = data?.series;
    const dates = series?.dates;
    const hasSeries = Boolean(dates && dates.length > 1);
    const days = data?.days || 0;
    const asOf = data?.as_of ? isoToBr(data.as_of) : '';
    const rangeLabel = data ? `${days} ${days === 1 ? 'dia' : 'dias'} · vs. ${days} ${days === 1 ? 'dia anterior' : 'dias anteriores'} · posição em ${asOf}` : '';

    // Tendência no formato do KpiTickerCard: total, total do período anterior e série diária.
    const trend = (curVal, prevVal, seriesKey) => {
        if (curVal == null || prevVal == null) return undefined;
        return { total: curVal, previous_total: prevVal, series: hasSeries && seriesKey ? series[seriesKey] : undefined };
    };

    const attention = data?.attention;
    const catalog = cur?.active_products || 0;

    // ------------------------------------------------------------------ mobile
    if (isMobileShell) {
        const top = (data?.top_sellers || []).map((p) => ({ name: p.name, value: p.units, text: `${fmtInt(p.units)} un` }));
        const cats = (data?.categories || []).map((c) => ({ name: c.category, value: c.revenue_cents, text: fmtBrl0(c.revenue_cents) }));
        return (
            <PageScaffold title="Dashboard de Produtos" titlePageTarget="Produtos" links={[{ title: 'Dashboard', link: '' }]}>
                {failed ? (
                    <KitCard><KitEmptyNote>Não foi possível carregar os indicadores de produtos</KitEmptyNote></KitCard>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <IndicatorGlossaryButton items={GLOSSARY_ITEMS} fullScreen />
                        </Box>
                        <KpiGrid first>
                            <Kpi grad label="Receita de produtos" icon="dollar" value={fmtBrl(cur?.revenue_cents)} loading={loading} />
                            <Kpi label="Lucro líquido" icon="wallet" value={formatMoneyShort((cur?.net_cents || 0) / 100)} loading={loading} delta={{ text: `${fmtPct1((cur?.margin_bp || 0) / 100)} de margem`, tone: 'up' }} />
                            <Kpi label="Em ruptura" icon="alert" value={fmtInt(attention?.zero)} loading={loading} delta={{ text: `${fmtInt(attention?.low)} com estoque baixo`, tone: 'warn' }} />
                            <Kpi label="Capital parado" icon="dollar" value={formatMoneyShort((cur?.parked_cents || 0) / 100)} loading={loading} delta={{ text: `${fmtInt(cur?.no_sale)} sem venda`, tone: 'warn' }} />
                        </KpiGrid>
                        <SecH title="Mais vendidos" />
                        <KitCard>{top.length ? <HBars items={top} /> : <KitEmptyNote>Sem vendas no período</KitEmptyNote>}</KitCard>
                        <SecH title="Receita por categoria" />
                        <KitCard>{cats.length ? <HBars items={cats} valueWidth={80} /> : <KitEmptyNote>Sem vendas no período</KitEmptyNote>}</KitCard>
                    </>
                )}
            </PageScaffold>
        );
    }

    // ----------------------------------------------------------------- desktop
    const gradient = `linear-gradient(90deg, ${theme.palette.brand2.main}, ${theme.palette.primary.main})`;
    const buckets = hasSeries ? buildBuckets(dates) : { groups: [], unit: 'dia' };
    const rev = buckets.groups.map((g) => sumAt(series.revenue_cents, g.idx));
    const cost = buckets.groups.map((g) => sumAt(series.cost_cents, g.idx));
    const margin = rev.map((r, i) => (r > 0 ? ((r - cost[i]) / r) * 100 : null));
    const hasSales = rev.some((v) => v > 0);
    const targetPct = cur?.margin_target_bp ? cur.margin_target_bp / 100 : 0;
    const costPct = cur?.revenue_cents > 0 ? (cur.cost_cents / cur.revenue_cents) * 100 : 0;
    const avgPrice = cur?.units > 0 ? cur.revenue_cents / cur.units : 0;

    const topSellers = data?.top_sellers || [];
    const abc = data?.abc;
    const abcColors = [theme.palette.success.main, theme.palette.warning.main, theme.palette.text.disabled];
    const abcText = [
        'Carro-chefe · proteger estoque e preço',
        'Intermediários · manter cobertura',
        'Cauda longa · avaliar descontinuar',
    ];
    const categories = data?.categories || [];
    const catColors = [theme.palette.primary.main, theme.palette.warning.main, theme.palette.secondary.main, theme.palette.brand2.main, theme.palette.text.disabled, theme.palette.text.disabled];
    const below = data?.below_margin;
    const lowStock = data?.low_stock;
    const noSale = data?.no_sale;

    // Pareto: segmento colorido pela classe (limite 80% / 95% do acumulado) do ponto de partida.
    const paretoW = 300;
    const paretoH = 150;
    const px = (x) => (x / 100) * paretoW;
    const py = (y) => paretoH - 8 - (y / 100) * (paretoH - 16);
    const paretoSegments = (abc?.curve || []).slice(1).map((pt, i) => {
        const a = abc.curve[i];
        const col = a.y < 80 ? abcColors[0] : a.y < 95 ? abcColors[1] : abcColors[2];
        return { key: i, col, pts: `${px(a.x)},${py(a.y)} ${px(pt.x)},${py(pt.y)}` };
    });

    const STOCK_LAYOUT_ID = {
        pstk: 'estoque', pgiro: 'giro', pcov: 'cobertura', pact: 'ativos', plow: 'estoque-baixo', pzero: 'ruptura', pns: 'sem-venda', ppark: 'capital-parado',
        pbm: 'abaixo-margem', pgain: 'ganho-potencial', pneg: 'prejuizo', ppro: 'promocao',
    };
    const stockCard = (id, props) => <Indicator key={id} id={STOCK_LAYOUT_ID[id]}><KpiTickerCard uid={id} {...props} loading={loading} /></Indicator>;

    const attentionNode = (id, node) => (
        <Indicator id={id}>{loading || !attention ? <Skeleton variant="rounded" height={62} sx={{ borderRadius: '12px' }} /> : node}</Indicator>
    );

    return (
        <PageScaffold
            title="Dashboard de Produtos"
            titlePageTarget="Produtos"
            links={[{ title: 'Dashboard', link: '' }]}
            severity={severity}
            message={message}
            setOpenSnackbar={setOpenSnackbar}
            openSnackbar={openSnackbar}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                            <Tag size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Dashboard de Produtos
                                <DashboardLayoutChip layout={layout} />
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Desempenho de vendas e margem, saúde do estoque e oportunidades de preço, vs. período anterior.
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        {!failed && <DashboardPersonalizeButton layout={layout} />}
                        <DesktopPeriodSelector
                            start={startDate}
                            end={endDate}
                            onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                            onRefresh={loadData}
                            loading={loading}
                            dualMonth
                        />
                    </Box>
                </Box>

                {failed && !loading && (
                    <EmptyMiniState
                        icon={<Tag size={22} strokeWidth={2} />}
                        title="Não foi possível carregar os indicadores"
                        subtitle="Tente atualizar o período em instantes"
                    />
                )}

                {!failed && (
                    <>
                        <DashboardEditBar layout={layout} />
                        {/* Cabeçalho do board: período + glossário. Alertas de "atenção agora" são posição atual (independem do período). */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '10px', mt: '4px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {data && days > 0 && (
                                    <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main', display: 'inline-block' }} />
                                        {rangeLabel}
                                    </Typography>
                                )}
                                <IndicatorGlossaryButton items={GLOSSARY_ITEMS} />
                            </Box>
                            <Typography sx={{ fontSize: '11px', color: 'text.disabled', fontWeight: 500 }}>Variações comparam com o período anterior de mesmo tamanho</Typography>
                        </Box>

                        <DashboardBoard layout={layout}>
                            {/* Cada item leva à tela relacionada (se houver permissão). */}
                            {attentionNode('a-ruptura', (
                                <AttentionItem color={theme.palette.error.main} bg={theme.palette.error.bg} icon={<Ban size={17} strokeWidth={2} />} value={fmtInt(attention?.zero)} label="produtos em ruptura"
                                    to={PRODUCTS_LIST} allowed={hasPermission('product.view')} title="Abrir a lista de produtos" />
                            ))}
                            {attentionNode('a-baixo', (
                                <AttentionItem color={theme.palette.warning.main} bg={theme.palette.warning.bg} icon={<AlertTriangle size={17} strokeWidth={2} />} value={fmtInt(attention?.low)} label="com estoque baixo"
                                    to={REPORT_STOCK} allowed={hasPermission('report.stock')} title="Abrir o relatório de estoque" />
                            ))}
                            {attentionNode('a-margem', (
                                <AttentionItem color={theme.palette.error.main} bg={theme.palette.error.bg} icon={<TrendingDown size={17} strokeWidth={2} />} value={fmtInt(attention?.below_margin)} label="abaixo da margem esperada"
                                    to={REPORT_PRODUCTS} allowed={hasPermission('report.product')} title="Abrir o relatório de produtos (aba Abaixo da margem)" />
                            ))}
                            {attentionNode('a-parado', (
                                <AttentionItem color={theme.palette.secondary.main} bg={theme.palette.secondary.bg} icon={<Lock size={17} strokeWidth={2} />} value={fmtBrl0(attention?.parked_cents)} label="de capital parado no período"
                                    to={REPORT_PRODUCTS} allowed={hasPermission('report.product')} title="Abrir o relatório de produtos (produtos sem venda)" />
                            ))}

                            {/* Grupo A — vendas e margem. */}
                            <Indicator id="receita">
                            <KpiTickerCard
                                hero large uid="prev" kind="money"
                                label="Receita de produtos" help={HELP.revenue}
                                icon={<DollarSign size={13} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main"
                                data={trend(cur?.revenue_cents, prev?.revenue_cents, 'revenue_cents')} days={dates}
                                value={cur?.revenue_cents || 0} loading={loading}
                                footer={cur && (
                                    <>
                                        <Box title="Composição da receita: custo x lucro líquido" sx={{ display: 'flex', height: 8, borderRadius: '6px', overflow: 'hidden', gap: '2px', bgcolor: 'background.subtle', mb: '6px' }}>
                                            <Box sx={{ width: `${Math.min(100, costPct)}%`, bgcolor: 'secondary.main' }} />
                                            <Box sx={{ width: `${Math.max(0, 100 - costPct)}%`, bgcolor: 'success.main' }} />
                                        </Box>
                                        <SummaryRow
                                            icon={<Tag size={13} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main"
                                            label={<>Custo dos produtos <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500, ml: '2px' }}>{fmtPct1(costPct)} da receita</Box></>}
                                            value={fmtBrl0(cur.cost_cents)} loading={loading}
                                        />
                                        <SummaryRow
                                            icon={<TrendingUp size={13} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main"
                                            label={<>Lucro líquido <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500, ml: '2px' }}>{fmtPct1((cur.margin_bp || 0) / 100)} · margem média</Box></>}
                                            value={fmtBrl0(cur.net_cents)} loading={loading}
                                        />
                                        <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', mt: '4px', '& b': { color: 'text.secondary' } }}>
                                            <b>{fmtInt(cur.units)}</b> unidades · preço médio <b>{fmtBrl(avgPrice)}</b> · receita por dia <b>{fmtBrl0(days > 0 ? cur.revenue_cents / days : 0)}</b>
                                        </Typography>
                                    </>
                                )}
                            />
                            </Indicator>
                            <Indicator id="lucro">
                            <KpiTickerCard uid="pnet" kind="money" label="Lucro líquido" help={HELP.net} icon={<TrendingUp size={11} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main"
                                data={trend(cur?.net_cents, prev?.net_cents, 'net_cents')} days={dates} value={cur?.net_cents || 0} loading={loading}
                                note={<>Receita − custo · <b>{fmtPct1((cur?.margin_bp || 0) / 100)}</b> de margem</>} />
                            </Indicator>
                            <Indicator id="margem">
                            <KpiTickerCard uid="pmg" kind="pct" label="Margem média" help={HELP.margin} icon={<Percent size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main"
                                data={trend(cur?.margin_bp, prev?.margin_bp, 'margin_bp')} days={dates} value={cur?.margin_bp || 0} loading={loading}
                                note={targetPct > 0 ? <>Margem esperada média das categorias: <b>{fmtPct1(targetPct)}</b></> : <>Sem margem esperada configurada nas categorias</>} />
                            </Indicator>
                            <Indicator id="unidades">
                            <KpiTickerCard uid="punits" kind="units" label="Unidades vendidas" help={HELP.units} icon={<Package size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="brand2.main"
                                data={trend(cur?.units, prev?.units, 'units')} days={dates} value={cur?.units || 0} loading={loading}
                                note={<>Preço médio por unidade <b>{fmtBrl(avgPrice)}</b></>} />
                            </Indicator>
                            <Indicator id="custo">
                            <KpiTickerCard inverse uid="pcost" kind="money" label="Custo dos produtos vendidos" help={HELP.cost} icon={<Tag size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main"
                                data={trend(cur?.cost_cents, prev?.cost_cents, 'cost_cents')} days={dates} value={cur?.cost_cents || 0} loading={loading}
                                note={<><b>{fmtPct1(costPct)}</b> da receita · subir é pior</>} />
                            </Indicator>

                            {/* Grupo B — estoque e capital imobilizado. */}
                            {stockCard('pstk', {
                                kind: 'money', neutral: true, estimated: true,
                                label: 'Valor em estoque (custo)', help: HELP.stockValue, icon: <Layers size={11} strokeWidth={2} />, iconBg: 'info.bg', iconFg: 'primary.main',
                                data: trend(cur?.stock_cost_cents, prev?.stock_cost_cents, 'stock_cost_cents'), days: dates, value: cur?.stock_cost_cents || 0,
                                note: <>A preço de venda <b>{fmtBrl0(cur?.stock_sale_cents)}</b> · potencial <b>{fmtBrl0((cur?.stock_sale_cents || 0) - (cur?.stock_cost_cents || 0))}</b></>,
                            })}
                            {stockCard('pgiro', {
                                kind: 'ratio', estimated: true, estimatedHint: 'Estoque médio reconstruído pelas movimentações, com custo atual',
                                label: 'Giro de estoque', help: HELP.turnover, icon: <RefreshCw size={11} strokeWidth={2} />, iconBg: 'success.bg', iconFg: 'success.main',
                                data: trend(cur?.giro, prev?.giro, 'giro'), days: dates, value: cur?.giro ?? null,
                                note: <>Custo vendido ÷ estoque médio no período</>,
                            })}
                            {stockCard('pcov', {
                                kind: 'days', neutral: true, estimated: true, estimatedHint: 'Estoque médio reconstruído pelas movimentações, com custo atual',
                                label: 'Cobertura de estoque', help: HELP.coverage, icon: <Clock size={11} strokeWidth={2} />, iconBg: 'info.bg', iconFg: 'brand2.main',
                                data: trend(cur?.coverage_days, prev?.coverage_days, 'coverage_days'), days: dates, value: cur?.coverage_days ?? null,
                                note: <>Quanto o estoque dura no ritmo atual de vendas</>,
                            })}
                            {stockCard('pact', {
                                kind: 'units', neutral: true,
                                label: 'Produtos ativos', help: HELP.activeProducts, icon: <Package size={11} strokeWidth={2} />, iconBg: 'info.bg', iconFg: 'primary.main',
                                value: cur?.active_products || 0,
                                footer: <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>Cadastrados e disponíveis para venda</Typography>,
                            })}
                            {stockCard('plow', {
                                kind: 'units', inverse: true, hideSpark: true,
                                label: 'Estoque baixo', help: HELP.lowStock, icon: <AlertTriangle size={11} strokeWidth={2} />, iconBg: 'warning.bg', iconFg: 'warning.main',
                                data: trend(cur?.low_stock, prev?.low_stock), value: cur?.low_stock || 0,
                                note: <>Abaixo do estoque mínimo · <b>{fmtPct1(pctOf(cur?.low_stock, catalog))}</b> do catálogo</>,
                            })}
                            {stockCard('pzero', {
                                kind: 'units', inverse: true, hideSpark: true,
                                label: <>Produtos em ruptura <Box component="small" sx={{ fontSize: '10px', color: 'text.disabled', fontWeight: 500 }}>· saldo zerado</Box></>,
                                help: HELP.zeroStock,
                                icon: <Ban size={11} strokeWidth={2} />, iconBg: 'error.bg', iconFg: 'error.main',
                                data: trend(cur?.zero_stock, prev?.zero_stock), value: cur?.zero_stock || 0,
                                note: <>Vendas perdidas enquanto não repõe</>,
                            })}
                            {stockCard('pns', {
                                kind: 'units', inverse: true, hideSpark: true,
                                label: 'Sem venda no período', help: HELP.noSale, icon: <Archive size={11} strokeWidth={2} />, iconBg: 'secondary.bg', iconFg: 'secondary.main',
                                data: trend(cur?.no_sale, prev?.no_sale), value: cur?.no_sale || 0,
                                note: <><b>{fmtPct1(pctOf(cur?.no_sale, catalog))}</b> do catálogo não saiu</>,
                            })}
                            {stockCard('ppark', {
                                kind: 'money', inverse: true, hideSpark: true,
                                label: 'Capital parado', help: HELP.parked, icon: <Lock size={11} strokeWidth={2} />, iconBg: 'error.bg', iconFg: 'error.main',
                                data: trend(cur?.parked_cents, prev?.parked_cents), value: cur?.parked_cents || 0,
                                note: <>Custo do estoque sem venda no período</>,
                            })}

                            {/* Grupo C — preço e margem. */}
                            {stockCard('pbm', {
                                kind: 'units', inverse: true,
                                label: <>Abaixo da margem esperada <Box component="small" sx={{ fontSize: '10px', color: 'text.disabled', fontWeight: 500 }}>· por categoria</Box></>,
                                help: HELP.belowMargin,
                                icon: <TrendingDown size={11} strokeWidth={2} />, iconBg: 'error.bg', iconFg: 'error.main',
                                value: cur?.below_margin || 0,
                                footer: <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', '& b': { color: 'text.secondary' } }}><b>{fmtPct1(pctOf(cur?.below_margin, catalog))}</b> do catálogo</Typography>,
                            })}
                            {stockCard('pgain', {
                                kind: 'money',
                                label: 'Ganho potencial c/ preços sugeridos', help: HELP.potentialGain, icon: <DollarSign size={11} strokeWidth={2} />, iconBg: 'success.bg', iconFg: 'success.main',
                                value: cur?.below_margin_gain_cents || 0,
                                footer: <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', '& b': { color: 'text.secondary' } }}>Nos <b>{fmtInt(cur?.below_margin)}</b> produtos abaixo da margem, no volume vendido do período</Typography>,
                            })}
                            {stockCard('pneg', {
                                kind: 'units', inverse: true, hideSpark: true,
                                label: <>Vendendo com prejuízo <Box component="small" sx={{ fontSize: '10px', color: 'text.disabled', fontWeight: 500 }}>· margem &lt; 0</Box></>,
                                help: HELP.losing,
                                icon: <AlertTriangle size={11} strokeWidth={2} />, iconBg: 'error.bg', iconFg: 'error.main',
                                data: trend(cur?.losing, prev?.losing), value: cur?.losing || 0,
                                note: <>Receita abaixo do custo no período · corrigir primeiro</>,
                            })}
                            {stockCard('ppro', {
                                kind: 'units', neutral: true,
                                label: 'Produtos em promoção', help: HELP.promotions, icon: <Gift size={11} strokeWidth={2} />, iconBg: 'warning.bg', iconFg: 'warning.main',
                                value: cur?.promotions || 0,
                                footer: <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>Promoções vigentes</Typography>,
                            })}

                            {/* Receita x custo + margem no tempo. */}
                            <Indicator id="grafico-receita-custo">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<BarChart3 size={18} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" title="Receita x custo" help={HELP.revenueCostChart}
                                        subtitle={`Por ${buckets.unit} · ${startDate.format('DD/MM/YYYY')} a ${endDate.format('DD/MM/YYYY')}`} loading={loading} />
                                    <Box sx={{ height: 230, width: '100%' }}>
                                        {loading ? <Skeleton variant="rounded" width="100%" height={210} /> : hasSales ? (
                                            <BarChart
                                                height={210}
                                                margin={{ top: 12, right: 8, bottom: 24, left: 8 }}
                                                borderRadius={4}
                                                categoryGapRatio={0.35}
                                                hideLegend
                                                xAxis={[{ scaleType: 'band', data: buckets.groups.map((g) => g.label), disableLine: true, disableTicks: true, tickLabelStyle: { fontSize: 10, fontWeight: 600, fill: theme.palette.text.disabled } }]}
                                                yAxis={[{ disableLine: true, disableTicks: true, valueFormatter: (v) => fmtBrlShort(v * 100) }]}
                                                series={[
                                                    { data: rev.map((v) => v / 100), label: 'Receita', color: theme.palette.primary.main, valueFormatter: (v) => fmtBrl(Math.round((v || 0) * 100)) },
                                                    { data: cost.map((v) => v / 100), label: 'Custo', color: theme.palette.secondary.main, valueFormatter: (v, ctx) => `${fmtBrl(Math.round((v || 0) * 100))} · lucro líquido ${fmtBrl(rev[ctx?.dataIndex] - cost[ctx?.dataIndex])}` },
                                                ]}
                                                sx={chartSx(theme)}
                                            />
                                        ) : (
                                            <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem vendas de produtos no período" />
                                        )}
                                    </Box>
                                    {!loading && hasSales && (
                                        <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
                                            <ChartLegendItem color={theme.palette.primary.main} label="Receita" />
                                            <ChartLegendItem color={theme.palette.secondary.main} label="Custo" />
                                            <Typography sx={{ ml: 'auto', fontSize: '11.5px', color: 'text.disabled' }}>Lucro líquido = receita − custo (no tooltip)</Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                            </Indicator>
                            <Indicator id="grafico-margem">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<Percent size={18} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" title="Margem média ao longo do tempo" help={HELP.marginChart}
                                        subtitle={`${targetPct > 0 ? `Linha tracejada = margem esperada média (${fmtPct1(targetPct)}) · ` : ''}por ${buckets.unit}`} loading={loading} />
                                    <Box sx={{ height: 230, width: '100%' }}>
                                        {loading ? <Skeleton variant="rounded" width="100%" height={210} /> : hasSales ? (
                                            <LineChart
                                                height={210}
                                                margin={{ top: 12, right: 12, bottom: 24, left: 8 }}
                                                hideLegend
                                                grid={{ horizontal: true }}
                                                xAxis={[{ scaleType: 'point', data: buckets.groups.map((g) => g.label), disableLine: true, disableTicks: true, tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled } }]}
                                                yAxis={[{ id: 'margin', disableLine: true, disableTicks: true, valueFormatter: (v) => `${Math.round(v)}%` }]}
                                                series={[{ data: margin, label: 'Margem', color: theme.palette.secondary.main, showMark: buckets.groups.length <= 16, connectNulls: true, valueFormatter: (v) => (v == null ? '—' : fmtPct1(v)) }]}
                                                sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider }, '& .MuiMarkElement-root': { fill: theme.palette.background.paper, strokeWidth: 2 } }}
                                            >
                                                {targetPct > 0 && <ChartsReferenceLine y={targetPct} axisId="margin" lineStyle={{ stroke: theme.palette.error.main, strokeDasharray: '4 4' }} />}
                                            </LineChart>
                                        ) : (
                                            <EmptyMiniState icon={<Percent size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem vendas de produtos no período" />
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                            </Indicator>

                            {/* Mais vendidos + Curva ABC. */}
                            <Indicator id="mais-vendidos">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<TrendingUp size={18} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" title="Produtos mais vendidos" help={HELP.topSellers} subtitle="Top 5 por quantidade no período" loading={loading}
                                        right={hasPermission('report.product') ? <ReportLink to={REPORT_PRODUCTS} title="Abrir o Relatório de Produtos" /> : null} />
                                    {loading ? [1, 2, 3, 4, 5].map((i) => <BarRow key={i} loading lead={<Skeleton variant="rounded" width={24} height={24} />} />) : topSellers.length ? topSellers.map((p, i) => (
                                        <BarRow key={p.product_id} lead={<RankNum n={i + 1} />} label={p.name} badge={<Pill tone="info">{p.category}</Pill>}
                                            barWidth={topSellers[0].units > 0 ? (p.units / topSellers[0].units) * 100 : 0} barColor={gradient}
                                            value={`${fmtInt(p.units)} un`} sub={`${fmtBrl0(p.revenue_cents)} · margem ${fmtPct1(p.margin_rate)}`} />
                                    )) : <EmptyMiniState icon={<Package size={22} strokeWidth={2} />} title="Sem vendas no período" subtitle="Ajuste o período acima" />}
                                </CardContent>
                            </Card>
                            </Indicator>
                            <Indicator id="curva-abc">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<PieChart size={18} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" title="Curva ABC de produtos" help={HELP.abc} subtitle="Participação na receita do período" loading={loading} />
                                    {loading ? <Skeleton variant="rounded" height={220} /> : abc && abc.products > 0 ? (
                                        <>
                                            <Box sx={{ display: 'flex', height: 12, borderRadius: '7px', overflow: 'hidden', gap: '2px', my: '6px 12px' }}>
                                                {abc.classes.map((c, i) => <Box key={c.class} title={`Classe ${c.class}: ${fmtPct1(c.revenue_share)} da receita`} sx={{ width: `${c.revenue_share}%`, bgcolor: abcColors[i] }} />)}
                                            </Box>
                                            {abc.classes.map((c, i) => (
                                                <Box key={c.class} sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: 1, fontSize: '12.5px', color: 'text.secondary', '& + &': { borderTop: '1px dashed', borderColor: 'divider' } }}>
                                                    <Box sx={{ width: 26, height: 26, borderRadius: '8px', fontWeight: 800, fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff', bgcolor: abcColors[i] }}>{c.class}</Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography component="b" sx={{ display: 'block', fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                                                            {fmtInt(c.products)} {c.products === 1 ? 'produto' : 'produtos'} <Box component="span" sx={{ fontWeight: 500, color: 'text.disabled' }}>· {fmtPct1(c.products_share)} dos vendidos</Box>
                                                        </Typography>
                                                        {abcText[i]}
                                                    </Box>
                                                    <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                                        <Typography component="b" sx={{ display: 'block', fontSize: '13.5px', fontWeight: 800, color: 'text.primary' }}>{fmtBrl0(c.revenue_cents)}</Typography>
                                                        {fmtPct1(c.revenue_share)} da receita
                                                    </Box>
                                                </Box>
                                            ))}
                                            <Box sx={{ mt: 1.5 }}>
                                                <svg viewBox={`0 0 ${paretoW} ${paretoH}`} preserveAspectRatio="none" style={{ width: '100%', height: 150, overflow: 'visible', display: 'block' }}>
                                                    {[25, 50, 75].map((y) => <line key={y} x1="0" x2={paretoW} y1={py(y)} y2={py(y)} stroke={theme.palette.divider} strokeWidth="1" />)}
                                                    {paretoSegments.map((s) => <polyline key={s.key} fill="none" stroke={s.col} strokeWidth="2.5" strokeLinecap="round" points={s.pts} vectorEffect="non-scaling-stroke" />)}
                                                </svg>
                                            </Box>
                                            <Typography sx={{ fontSize: '10.5px', color: 'text.disabled', mt: 1 }}>Eixo X: % dos produtos vendidos · eixo Y: % acumulado da receita.</Typography>
                                        </>
                                    ) : <EmptyMiniState icon={<PieChart size={22} strokeWidth={2} />} title="Sem vendas no período" subtitle="A curva ABC precisa de vendas no período" />}
                                </CardContent>
                            </Card>
                            </Indicator>

                            {/* Categorias + Abaixo da margem. */}
                            <Indicator id="categorias">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<Layers size={18} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" title="Receita e margem por categoria" help={HELP.categories} subtitle="Onde está o dinheiro e onde ele rende mais" loading={loading} />
                                    {loading ? [1, 2, 3, 4].map((i) => <BarRow key={i} loading lead={<Skeleton variant="circular" width={10} height={10} />} />) : categories.length ? categories.map((c, i) => (
                                        <Box key={c.category} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: '10px', '& + &': { borderTop: '1px dashed', borderColor: 'divider' } }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: catColors[i % catColors.length], flexShrink: 0 }} />
                                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <Typography noWrap sx={{ fontSize: '13px', fontWeight: 600, color: 'text.secondary' }}>{c.category}</Typography>
                                                <Box sx={{ height: 5, borderRadius: '4px', bgcolor: 'background.subtle', overflow: 'hidden' }}>
                                                    <Box sx={{ height: '100%', borderRadius: '4px', width: `${categories[0].share_rate > 0 ? (c.share_rate / categories[0].share_rate) * 100 : 0}%`, bgcolor: catColors[i % catColors.length], transition: 'width .4s ease' }} />
                                                </Box>
                                            </Box>
                                            <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '1px', flexShrink: 0 }}>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 800, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>{fmtBrl0(c.revenue_cents)}</Typography>
                                                <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled' }}>{fmtPct1(c.share_rate)} da receita</Typography>
                                            </Box>
                                            <Box title="Margem média da categoria" sx={{ minWidth: 64, display: 'inline-flex', justifyContent: 'center' }}>
                                                <Pill tone={targetPct > 0 && c.margin_rate < targetPct ? 'warning' : 'success'}>{fmtPct1(c.margin_rate)}</Pill>
                                            </Box>
                                        </Box>
                                    )) : <EmptyMiniState icon={<Layers size={22} strokeWidth={2} />} title="Sem vendas no período" subtitle="Ajuste o período acima" />}
                                </CardContent>
                            </Card>
                            </Indicator>
                            <Indicator id="lista-abaixo-margem">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<TrendingDown size={18} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" title="Abaixo da margem esperada" help={HELP.belowMarginList}
                                        subtitle={below ? `${fmtInt(below.total)} ${below.total === 1 ? 'produto' : 'produtos'} abaixo da margem da categoria · ganho potencial ${fmtBrl0(below.gain_cents)}` : ''} loading={loading}
                                        right={hasPermission('report.product') ? <ReportLink to={REPORT_PRODUCTS} title="Abrir o Relatório de Produtos" /> : null} />
                                    {loading ? [1, 2, 3].map((i) => <ListRow key={i} avatar={<Skeleton variant="rounded" width={34} height={34} />} title={<Skeleton width={120} />} sub="" end={null} />) : below?.items?.length ? below.items.map((b) => (
                                        <ListRow key={b.product_id}
                                            avatar={<Badge bg={theme.palette.error.bg} fg={theme.palette.error.main}>{b.current_margin.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</Badge>}
                                            title={b.name}
                                            sub={<>Venda {fmtBrl(b.sale_cents)} → sugerido <b style={{ color: theme.palette.text.primary }}>{fmtBrl(b.suggested_cents)}</b></>}
                                            end={(
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                                        <Pill tone="error">{fmtPct1(b.current_margin)}</Pill>
                                                        <span style={{ fontSize: 11 }}>esperada {fmtPct1(b.expected_margin)}</span>
                                                    </Box>
                                                    {canApplyPrice && (
                                                        <Box component="button" type="button" title="Atualiza o preço de venda para o sugerido" onClick={() => setApplyProduct({ ...b, sale_price: fmtBrl(b.sale_cents), suggested_sale_price: fmtBrl(b.suggested_cents) })}
                                                            sx={{ border: '1px solid', borderColor: 'primary.main', bgcolor: 'info.bg', color: 'primary.main', fontSize: '11px', fontWeight: 700, px: '9px', py: '5px', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap', font: 'inherit', fontFamily: 'inherit', '&:hover': { bgcolor: 'primary.main', color: '#fff' } }}>
                                                            Aplicar
                                                        </Box>
                                                    )}
                                                </Box>
                                            )} />
                                    )) : <EmptyMiniState icon={<TrendingUp size={22} strokeWidth={2} />} title="Nenhum produto abaixo da margem" subtitle="Todos os produtos atendem à margem esperada" />}
                                    {!loading && below && below.total > below.items.length && (
                                        <FootNote icon={<Tag size={15} strokeWidth={2} />}>Mostrando {below.items.length} de <b>{fmtInt(below.total)}</b> · piores margens primeiro</FootNote>
                                    )}
                                </CardContent>
                            </Card>
                            </Indicator>

                            {/* Estoque baixo e ruptura + Sem venda. */}
                            <Indicator id="lista-estoque-baixo">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<AlertTriangle size={18} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" title="Estoque baixo e ruptura" help={HELP.lowStockList}
                                        subtitle={lowStock ? `${fmtInt(lowStock.zero)} em ruptura · ${fmtInt(lowStock.low)} com estoque baixo` : ''} loading={loading} />
                                    {loading ? [1, 2, 3].map((i) => <ListRow key={i} avatar={<Skeleton variant="rounded" width={34} height={34} />} title={<Skeleton width={120} />} sub="" end={null} />) : lowStock?.items?.length ? lowStock.items.map((p) => (
                                        <ListRow key={p.product_id}
                                            avatar={<Badge bg={p.status === 'zero' ? theme.palette.error.bg : theme.palette.warning.bg} fg={p.status === 'zero' ? theme.palette.error.main : theme.palette.warning.main}>{fmtInt(p.balance)}</Badge>}
                                            title={p.name}
                                            sub={<>Saldo {fmtInt(p.balance)} un · mínimo {fmtInt(p.min_quantity)} un</>}
                                            end={<><Pill tone={p.status === 'zero' ? 'error' : 'warning'}>{p.status === 'zero' ? 'Zerado' : 'Baixo'}</Pill><span style={{ fontSize: 11 }}>cobertura <b>{p.coverage_days == null ? '—' : fmtDays(p.coverage_days)}</b></span></>} />
                                    )) : <EmptyMiniState icon={<Package size={22} strokeWidth={2} />} title="Estoque saudável" subtitle="Nenhum produto em ruptura ou abaixo do mínimo" />}
                                    {!loading && lowStock && lowStock.zero + lowStock.low > 0 && (
                                        <FootNote icon={<Package size={15} strokeWidth={2} />}>
                                            Mostrando {lowStock.items.length} de <b>{fmtInt(lowStock.zero + lowStock.low)}</b> · zerados primeiro
                                            {hasPermission('report.stock') && <> · <Box component={RouterLink} to={REPORT_STOCK} sx={{ color: 'primary.main', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Abrir relatório de estoque</Box></>}
                                        </FootNote>
                                    )}
                                </CardContent>
                            </Card>
                            </Indicator>
                            <Indicator id="lista-sem-venda">
                            <Card>
                                <CardContent sx={{ p: 2.25 }}>
                                    <CardHeadRow icon={<Archive size={18} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" title="Sem venda no período" help={HELP.noSaleList}
                                        subtitle={noSale ? `${fmtInt(noSale.total)} ${noSale.total === 1 ? 'produto sem nenhuma venda' : 'produtos sem nenhuma venda'} em ${fmtInt(noSale.days)} ${noSale.days === 1 ? 'dia' : 'dias'} · ${fmtBrl0(noSale.parked_cents)} parados` : ''} loading={loading}
                                        right={hasPermission('report.product') ? <ReportLink to={REPORT_PRODUCTS} title="Abrir o Relatório de Produtos" /> : null} />
                                    {loading ? [1, 2, 3].map((i) => <ListRow key={i} avatar={<Skeleton variant="rounded" width={34} height={34} />} title={<Skeleton width={120} />} sub="" end={null} />) : noSale?.items?.length ? noSale.items.map((p) => (
                                        <ListRow key={p.product_id}
                                            avatar={<Badge bg={theme.palette.secondary.bg} fg={theme.palette.secondary.main}>{fmtInt(p.balance)}</Badge>}
                                            title={p.name}
                                            sub={<>Saldo {fmtInt(p.balance)} un · custo {fmtBrl(p.cost_cents)}</>}
                                            end={<><b style={{ fontSize: 12.5 }}>{fmtBrl0(p.parked_cents)}</b><span style={{ fontSize: 11 }}>{p.days_since_sale == null ? 'nunca vendeu' : `sem venda há ${fmtInt(p.days_since_sale)} dias`}</span></>} />
                                    )) : <EmptyMiniState icon={<Archive size={22} strokeWidth={2} />} title="Nenhum produto parado" subtitle="Todos os produtos com saldo tiveram venda no período" />}
                                    {!loading && noSale && noSale.with_stock > 0 && (
                                        <FootNote icon={<Info size={15} strokeWidth={2} />}>
                                            Mostrando {noSale.items.length} de <b>{fmtInt(noSale.with_stock)}</b> com saldo · maiores valores parados primeiro · sugestão: promoção ou kit para liberar capital
                                        </FootNote>
                                    )}
                                </CardContent>
                            </Card>
                            </Indicator>
                        </DashboardBoard>
                    </>
                )}
            </Box>

            <DialogForm
                open={Boolean(applyProduct)}
                iconType="warning"
                title="Aplicar preço sugerido?"
                subtitle={applyProduct?.name}
                content={applyProduct ? 'O preço de venda será atualizado com o valor recalculado pelo servidor neste momento, com base no custo e na margem esperada vigentes. Confira os valores e confirme se deseja prosseguir.' : ''}
                secondaryContent={applyProduct ? `Valor de venda atual: ${applyProduct.sale_price} · Preço sugerido (referência na listagem): ${applyProduct.suggested_sale_price} · Margem atual: ${fmtPct1(applyProduct.current_margin)} · Margem esperada: ${fmtPct1(applyProduct.expected_margin)}` : ''}
                confirmButtonText="Confirmar"
                cancelButtonText="Cancelar"
                loading={applyLoading}
                loadingText="Aplicando…"
                onClose={() => {
                    if (applyLoading) return;
                    setApplyProduct(null);
                }}
                onSubmit={() => { void handleApplySuggestedPrice(); }}
            />
        </PageScaffold>
    );
}
